<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import {
  Sparkles,
  Sun,
  Moon,
  Monitor,
  ChevronDown,
  LogOut,
  UserCircle,
  Code2,
  Globe,
  Check,
  Bell,
  BookOpen,
  ExternalLink,
  LifeBuoy,
  Menu,
} from 'lucide-vue-next';
import BrandMark from './BrandMark.vue';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { logout as apiLogout } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/uiStore';
import { useNotificationsStore } from '@/stores/notificationsStore';
import { useDeveloperMode } from '@/composables/useDeveloperMode';
import { useTheme } from '@/composables/useTheme';
import { useLocale } from '@/composables/useLocale';
import type { SupportedLocale } from '@/i18n';

const { t } = useI18n();
const auth = useAuthStore();
const ui = useUiStore();
const notifications = useNotificationsStore();
const dev = useDeveloperMode();
const theme = useTheme();
const locale = useLocale();
const router = useRouter();

onMounted(() => {
  // Drive the bell-and-dot. Polling is idempotent across mounts.
  notifications.start();
});
onBeforeUnmount(() => {
  notifications.stop();
});

async function openMostRecentNotification(): Promise<void> {
  const next = notifications.list.find((n) => !n.readAt);
  if (!next) return;
  // Navigate first, then mark read — the user lands on the linked page
  // immediately and the bell clears in the background.
  await router.push(next.actionUrl);
  void notifications.markRead(next.id);
}

const userLabel = computed(() => {
  if (auth.user?.displayName) return auth.user.displayName;
  if (auth.user?.email) return auth.user.email;
  if (auth.devShim) return `dev:${auth.devShim.userId.slice(0, 6)}`;
  return t('topbar.profile');
});

const userEmail = computed(() => auth.user?.email ?? null);

const currentLocaleLabel = computed(
  () => locale.options.find((o) => o.code === locale.current.value)?.label ?? locale.current.value,
);

async function logout(): Promise<void> {
  await apiLogout();
  await router.push({ name: 'login' });
}

function setTheme(value: 'light' | 'dark' | 'auto'): void {
  theme.setPreference(value);
}

function setLocale(code: SupportedLocale): void {
  locale.set(code);
}

function openWebsite(): void {
  window.open('https://aitelier.org', '_blank', 'noopener');
}
</script>

<template>
  <header class="glass sticky top-0 z-20 border-b border-border h-14 flex items-center justify-between px-4 md:px-6">
    <div class="flex items-center gap-2 min-w-0">
      <button
        type="button"
        class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-text-muted hover:text-text hover:bg-bg transition-colors lg:hidden"
        :aria-label="t('topbar.openMenu')"
        @click="ui.toggleMobileNav()"
      >
        <Menu class="h-5 w-5" />
      </button>
      <BrandMark size="sm" class="lg:hidden" />
      <slot name="left" />
    </div>

    <div class="flex items-center gap-1 sm:gap-2">
      <button
        type="button"
        class="relative inline-flex items-center justify-center h-9 w-9 rounded-lg text-text-muted hover:text-text hover:bg-bg transition-colors"
        :aria-label="t('topbar.notifications')"
        :title="t('topbar.notifications')"
        :disabled="notifications.unreadCount === 0"
        @click="openMostRecentNotification"
      >
        <Bell class="h-4 w-4" />
        <span
          v-if="notifications.unreadCount > 0"
          class="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500"
          aria-hidden="true"
        />
      </button>

      <Button
        variant="ghost"
        size="sm"
        class="hidden md:inline-flex gap-2"
        @click="ui.toggleCopilot()"
      >
        <Sparkles class="h-4 w-4" />
        <span>{{ t('topbar.assistant') }}</span>
        <kbd class="ml-1 inline-flex h-5 items-center rounded border border-border px-1.5 text-[10px] text-text-muted">⌘K</kbd>
      </Button>

      <Button
        variant="ghost"
        size="icon"
        class="md:hidden"
        :aria-label="t('topbar.assistant')"
        @click="ui.toggleCopilot()"
      >
        <Sparkles class="h-4 w-4" />
      </Button>

      <DropdownMenu align="end">
        <template #trigger>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-text-muted hover:text-text hover:bg-bg transition-colors"
            :aria-label="t('topbar.languageMenu')"
            :title="t('topbar.languageMenu')"
          >
            <Globe class="h-4 w-4" />
            <span class="hidden sm:inline text-[12px] font-medium uppercase tracking-wide">
              {{ locale.current.value }}
            </span>
            <ChevronDown class="h-3 w-3" />
          </button>
        </template>
        <DropdownMenuLabel>{{ t('topbar.languageMenu') }}</DropdownMenuLabel>
        <DropdownMenuItem
          v-for="opt in locale.options"
          :key="opt.code"
          @select="setLocale(opt.code)"
        >
          <span class="flex-1">{{ opt.label }}</span>
          <Check v-if="locale.current.value === opt.code" class="h-3.5 w-3.5 text-accent" />
        </DropdownMenuItem>
      </DropdownMenu>

      <DropdownMenu align="end">
        <template #trigger>
          <button class="inline-flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-bg transition-colors">
            <Avatar :name="userLabel" size="sm" />
            <span class="hidden sm:inline text-[13px] font-medium text-text">{{ userLabel }}</span>
            <ChevronDown class="h-3.5 w-3.5 text-text-muted" />
          </button>
        </template>

        <DropdownMenuLabel>
          <div class="flex flex-col gap-0.5 normal-case tracking-normal">
            <span class="text-[13px] font-medium text-text">{{ userLabel }}</span>
            <span v-if="userEmail" class="text-[11px] text-text-muted">{{ userEmail }}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem @select="router.push('/account')">
          <UserCircle class="h-4 w-4 text-text-muted" />
          {{ t('topbar.account') }}
        </DropdownMenuItem>

        <DropdownMenuItem @select="ui.openWelcomeGuide()">
          <BookOpen class="h-4 w-4 text-text-muted" />
          {{ t('topbar.welcomeGuide') }}
        </DropdownMenuItem>

        <DropdownMenuItem @select="router.push('/support')">
          <LifeBuoy class="h-4 w-4 text-text-muted" />
          {{ t('topbar.support') }}
        </DropdownMenuItem>

        <DropdownMenuItem @select="openWebsite">
          <ExternalLink class="h-4 w-4 text-text-muted" />
          <span class="flex-1">{{ t('topbar.visitWebsite') }}</span>
          <span class="font-mono text-[11px] text-text-muted">aitelier.org</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>{{ t('topbar.theme') }}</DropdownMenuLabel>
        <DropdownMenuItem @select="setTheme('light')">
          <Sun class="h-4 w-4 text-text-muted" />
          <span class="flex-1">{{ t('topbar.themeLight') }}</span>
          <span v-if="theme.preference.value === 'light'" class="text-accent text-[12px]">✓</span>
        </DropdownMenuItem>
        <DropdownMenuItem @select="setTheme('dark')">
          <Moon class="h-4 w-4 text-text-muted" />
          <span class="flex-1">{{ t('topbar.themeDark') }}</span>
          <span v-if="theme.preference.value === 'dark'" class="text-accent text-[12px]">✓</span>
        </DropdownMenuItem>
        <DropdownMenuItem @select="setTheme('auto')">
          <Monitor class="h-4 w-4 text-text-muted" />
          <span class="flex-1">{{ t('topbar.themeAuto') }}</span>
          <span v-if="theme.preference.value === 'auto'" class="text-accent text-[12px]">✓</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem @select.prevent="dev.toggle()">
          <Code2 class="h-4 w-4 text-text-muted" />
          <span class="flex-1">{{ t('topbar.developerMode') }}</span>
          <Switch
            :model-value="dev.enabled.value"
            @update:model-value="dev.set"
            @click.stop
          />
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem @select="logout">
          <LogOut class="h-4 w-4 text-text-muted" />
          {{ t('topbar.logout') }}
        </DropdownMenuItem>
      </DropdownMenu>
    </div>
  </header>
</template>
